import { useCallback, useMemo, useState } from 'react';

export interface IUsePriceProductExpandParams {
  /** The products that stand for more than one variant — the only ones with anything to expand. */
  expandableBaseIDs: string[];
}

export interface IUsePriceProductExpandResult {
  isExpanded: (baseID: string) => boolean;
  isAnyExpanded: boolean;
  handleToggleExpand: (baseID: string) => void;
  handleCollapseAll: () => void;
}

/**
 * A product with several variants opens expanded, so what is tracked is which ones the merchant has
 * since closed. Storing it the other way round would need an effect to expand each product as its
 * variants land, and that effect would re-expand anything closed before the detail arrived.
 */
export const usePriceProductExpand = ({
  expandableBaseIDs,
}: IUsePriceProductExpandParams): IUsePriceProductExpandResult => {
  const [collapsedByBaseID, setCollapsedByBaseID] = useState<Record<string, boolean>>({});

  const isExpanded = useCallback(
    (baseID: string) => expandableBaseIDs.includes(baseID) && !collapsedByBaseID[baseID],
    [expandableBaseIDs, collapsedByBaseID],
  );

  const isAnyExpanded = useMemo(
    () => expandableBaseIDs.some((baseID) => !collapsedByBaseID[baseID]),
    [expandableBaseIDs, collapsedByBaseID],
  );

  const handleToggleExpand = useCallback((baseID: string) => {
    setCollapsedByBaseID((current) => ({ ...current, [baseID]: !current[baseID] }));
  }, []);

  const handleCollapseAll = useCallback(() => {
    setCollapsedByBaseID(Object.fromEntries(expandableBaseIDs.map((baseID) => [baseID, true])));
  }, [expandableBaseIDs]);

  return { isExpanded, isAnyExpanded, handleToggleExpand, handleCollapseAll };
};
